/**
 * UPT-2079 L3 (§5.5) — constantes COPIÉES des énumérations du schéma v1.1 (`module-spec.v1.1.schema.json`, jamais
 * importé en production : `catalogDefaults.test.ts` vérifie l'égalité avec `__tests__/fixtures/module-spec.v1.1.schema.json`)
 * et les neuf transformeurs de `TokenizingAttributeConverter.KNOWN_TRANSFORMERS` (rest). `mergeCatalog` rend les listes
 * du serveur (`GET /api/v1/module-spec/catalog`) quand elles sont présentes et non vides, sinon ces constantes.
 */
import type { DesignerCatalog } from '../data/contracts';

/** `$defs.field.type`. */
export const FIELD_TYPES: readonly string[] = [
  'text', 'textarea', 'number', 'amount', 'date', 'datetime', 'time', 'checkbox', 'select', 'radio', 'typeahead',
  'email', 'color', 'link', 'display', 'hidden', 'attachments', 'references', 'entityref',
];

/** `$defs.validator.type`. */
export const VALIDATOR_TYPES: readonly string[] = [
  'required', 'maxLength', 'minLength', 'isNumber', 'minValue', 'maxValue', 'pattern', 'notEqual', 'email', 'avsNumber',
];

/** `$defs.protection.classification`. */
export const CLASSIFICATION_LEVELS: readonly string[] = ['C1', 'C2', 'C3', 'C4'];

/** `$defs.coreReuse.capability`. */
export const CORE_CAPABILITIES: readonly string[] = [
  'attachments', 'tasks', 'risks', 'comments', 'tags', 'goals', 'entityLinks', 'macroplans', 'history', 'information',
  'classification', 'dashboard', 'onboarding', 'copilot', 'documentation',
];

/** `$defs.locale`. */
export const LOCALES: readonly string[] = ['fr', 'en', 'de', 'it', 'ar'];

/** `$defs.portal`. */
export const PORTALS: readonly string[] = ['portal', 'adminportal'];

/** `$defs.entity.kind`. */
export const ENTITY_KINDS: readonly string[] = ['master', 'transaction', 'reference', 'child'];

/** `$defs.relation.cardinality`. */
export const CARDINALITIES: readonly string[] = ['1-1', '1-N', 'N-1', 'N-N'];

/** `$defs.relation.missingModule` et `$defs.dataEntityRef.missingModule`. */
export const MISSING_MODULE: readonly string[] = ['hide', 'text', 'locked'];

/** `$defs.page.kind`. */
export const PAGE_KINDS: readonly string[] = ['dashboard', 'calendar', 'cockpit', 'wizard', 'report', 'board', 'other', 'custom'];

/** UPT-2079 L14b (schéma v1.2) — `$defs.page.layout`. */
export const PAGE_LAYOUTS: readonly string[] = ['full', 'centered', 'split', 'grid'];

/** UPT-2079 L14b — `$defs.page.slots[].binding`. */
export const SLOT_BINDINGS: readonly string[] = ['none', 'list', 'record', 'aggregate'];

/** UPT-2079 L14b — `$defs.page.actions[].kind`. */
export const ACTION_KINDS: readonly string[] = ['navigate', 'create', 'update', 'transition', 'custom'];

/** UPT-2079 L14b — bornes et motifs d'une page `custom` (schéma v1.2, `$defs.page`). */
export const CUSTOM_PAGE_LIMITS = {
  /** `reads` et `writes`. */
  entities: 20,
  slots: 12,
  actions: 20,
  slotDescription: 500,
  actionDescription: 300,
  sectionLabel: 120,
  selector: 120,
  /** `slots[].key` et `actions[].key` ; `slot` d'une action. */
  keyPattern: /^[a-z][a-z0-9-]{1,40}$/,
  /** `actions[].targetPage` et `actions[].transition`. */
  refPattern: /^[a-z][a-z0-9-]*$/,
  /** `prototypeRef.importRef` et `pieceRef`. */
  uuidPattern: /^[0-9a-f-]{36}$/,
} as const;

/** `$defs.module.category`. */
export const MODULE_CATEGORIES: readonly string[] = ['platform', 'business', 'compliance', 'engagement', 'finance', 'health', 'ai', 'other'];

/** `$defs.rule.kind`. */
export const RULE_KINDS: readonly string[] = ['validation', 'computed', 'default', 'visibility', 'side-effect'];

/** `$defs.field.mode`. */
export const FIELD_MODES: readonly string[] = ['EDIT', 'READONLY', 'HIDDEN'];

/** `$defs.screens.list.defaultSort.direction`. */
export const SORT_DIRECTIONS: readonly string[] = ['asc', 'desc'];

/** Les neuf transformeurs connus du serveur (`TokenizingAttributeConverter.KNOWN_TRANSFORMERS`). */
export const TRANSFORMERS: readonly string[] = [
  'encrypt-iban-fpe', 'tokenize-email-det', 'tokenize-national-id-det', 'mask-freetext-rnd', 'encrypt-freetext-rnd',
  'encrypt-phone-fpe', 'tokenize-person-name-det', 'tokenize-supplier-name-det', 'encrypt-api-key-fpe',
];

/** Transformeur posé par défaut quand `personalData` devient vrai (§5.3.1 : réversible le plus général). */
export const DEFAULT_TOKENIZE = 'encrypt-freetext-rnd';

/** Catalogue de démonstration (constantes + le seul module « installé » en démonstration). */
export const DEMO_CATALOG: DesignerCatalog = {
  schemaVersion: '1.1',
  fieldTypes: [...FIELD_TYPES],
  validators: [...VALIDATOR_TYPES],
  transformers: [...TRANSFORMERS],
  classificationLevels: [...CLASSIFICATION_LEVELS],
  coreCapabilities: [...CORE_CAPABILITIES],
  locales: [...LOCALES],
  modules: [{ code: 'pret-materiel', version: '1.0.0', ownerOrganisation: 'Démonstration', own: true, entities: ['loan', 'equipment'] }],
  conventions: '',
};

const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];

const pick = (remote: unknown, fallback: readonly string[]): string[] => {
  const list = strings(remote);
  return list.length > 0 ? list : [...fallback];
};

/** Catalogue effectif : listes du serveur quand elles existent, sinon les constantes ; `modules` du serveur (ou vide). */
export const mergeCatalog = (remote: DesignerCatalog | null | undefined): DesignerCatalog => {
  const source = (remote ?? {}) as Partial<DesignerCatalog>;
  const modules = Array.isArray(source.modules)
    ? source.modules
        .filter((entry) => entry && typeof entry === 'object' && typeof entry.code === 'string')
        .map((entry) => ({
          code: entry.code,
          version: typeof entry.version === 'string' ? entry.version : '',
          ownerOrganisation: typeof entry.ownerOrganisation === 'string' ? entry.ownerOrganisation : '',
          own: entry.own === true,
          entities: strings(entry.entities),
        }))
    : [];
  return {
    schemaVersion: typeof source.schemaVersion === 'string' ? source.schemaVersion : '1.1',
    fieldTypes: pick(source.fieldTypes, FIELD_TYPES),
    validators: pick(source.validators, VALIDATOR_TYPES),
    transformers: pick(source.transformers, TRANSFORMERS),
    classificationLevels: pick(source.classificationLevels, CLASSIFICATION_LEVELS),
    coreCapabilities: pick(source.coreCapabilities, CORE_CAPABILITIES),
    locales: pick(source.locales, LOCALES),
    modules,
    conventions: typeof source.conventions === 'string' ? source.conventions : '',
  };
};
